import { Modal, Stack, Group, Button, Select, Text, Paper, Loader, Center, Checkbox } from '@mantine/core';
import { IconX, IconCheck } from '@tabler/icons-react';
import MilkdownRenderer from '../shared/MilkdownRenderer';
import CrepeEditor from '../shared/CrepeEditor';
import { JobArtifact, useUpdateArtifact, useCompileArtifact } from '../../api/artifacts';
import { PdfStylesheet } from '../../api/stylesheets';
import { useState, useMemo } from 'react';

interface ResumeEditModalProps {
  opened: boolean;
  onClose: () => void;
  resume: JobArtifact | null;
  allStylesheets: PdfStylesheet[];
  defaultStylesheet: PdfStylesheet | null;
  jobId: string;
  onSuccess?: () => void;
}

export function ResumeEditModal({
  opened,
  onClose,
  resume,
  allStylesheets,
  defaultStylesheet,
  jobId,
  onSuccess,
}: ResumeEditModalProps) {
  const [markdown, setMarkdown] = useState('');
  const [stylesheetId, setStylesheetId] = useState<string | null>(null);
  const [createNewVersion, setCreateNewVersion] = useState(false);

  // Initialize state when modal opens or resume changes
  useMemo(() => {
    if (opened && resume) {
      setMarkdown(resume.content_markdown);
      setStylesheetId(resume.stylesheet_id || defaultStylesheet?.id || null);
      setCreateNewVersion(false);
    }
  }, [opened, resume, defaultStylesheet]);

  const updateMutation = useUpdateArtifact(resume?.id || '');
  const compileMutation = useCompileArtifact();

  // Determine current stylesheet
  const currentStylesheet = useMemo(() => {
    if (stylesheetId) {
      return allStylesheets.find((s) => s.id === stylesheetId) || defaultStylesheet;
    }
    return defaultStylesheet;
  }, [stylesheetId, allStylesheets, defaultStylesheet]);

  const handleSave = async () => {
    if (!resume || !jobId) return;

    const markdownChanged = markdown !== resume.content_markdown;
    const stylesheetChanged = stylesheetId !== (resume.stylesheet_id || defaultStylesheet?.id);

    if (!markdownChanged && !stylesheetChanged) {
      onClose();
      return;
    }

    try {
      if (createNewVersion && markdownChanged) {
        // Create a new version
        await compileMutation.mutateAsync({
          job_id: jobId,
          artifact_type: 'resume',
          name: `${resume.name}`,
          content_markdown: markdown,
          content_ids: resume.used_content_ids || [],
          stylesheet_id: stylesheetId || undefined,
          metadata: resume.metadata,
        });
      } else {
        // Update current version
        await updateMutation.mutateAsync({
          name: resume.name,
          content_markdown: markdownChanged ? markdown : undefined,
          stylesheet_id: stylesheetId || undefined,
          metadata: resume.metadata,
        });
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error saving resume:', error);
    }
  };

  const isLoading = updateMutation.isPending || compileMutation.isPending;
  const markdownChanged = markdown !== (resume?.content_markdown || '');

  if (!resume) return null;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={`Edit: ${resume.name} (v${resume.version})`}
      size="xl"
      fullScreen
    >
      <Stack gap="md" h="100%">
        {/* Stylesheet Selector */}
        {allStylesheets.length > 0 ? (
          <Select
            label="Stylesheet"
            placeholder="Select a stylesheet"
            data={allStylesheets.map((s) => ({
              value: s.id,
              label: s.name,
            }))}
            value={stylesheetId}
            onChange={setStylesheetId}
            searchable
            clearable
            description={currentStylesheet ? `Current: ${currentStylesheet.name}` : 'No stylesheet selected'}
          />
        ) : (
          <Text size="sm" c="dimmed">
            No stylesheets available. The resume will use default styling.
          </Text>
        )}

        {/* Version control option */}
        {markdownChanged && (
          <Checkbox
            label="Create a new version (instead of updating current version)"
            checked={createNewVersion}
            onChange={(e) => setCreateNewVersion(e.currentTarget.checked)}
            description="If unchecked, changes will update the current version"
          />
        )}

        {/* Editor area */}
        <Group grow align="flex-start" gap="md" style={{ flex: 1, minHeight: 0 }}>
          {/* Markdown Editor with Crepe */}
          <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
            <Text size="sm" fw={500}>
              Markdown Editor
            </Text>
            <div style={{ flex: 1, minHeight: 0 }}>
              <CrepeEditor
                content={markdown}
                onChange={setMarkdown}
                placeholder="Enter resume markdown..."
                variant="default"
              />
            </div>
          </Stack>

          {/* Live Preview */}
          <Stack gap="sm" style={{ flex: 1, minHeight: 0 }}>
            <Text size="sm" fw={500}>
              Preview
            </Text>
            <Paper
              p="md"
              radius="md"
              withBorder
              style={{
                flex: 1,
                overflowY: 'auto',
                backgroundColor: '#ffffff',
                color: '#000000',
              }}
            >
              <MilkdownRenderer
                content={markdown}
                variant="resume-preview"
                customStyles={currentStylesheet?.css_content}
              />
            </Paper>
          </Stack>
        </Group>

        {/* Action buttons */}
        <Group justify="flex-end" gap="sm">
          <Button variant="light" onClick={onClose}>
            Cancel
          </Button>
          <Button
            leftSection={isLoading ? <Loader size={16} /> : <IconCheck size={16} />}
            onClick={handleSave}
            loading={isLoading}
            disabled={isLoading || (!markdownChanged && stylesheetId === (resume.stylesheet_id || defaultStylesheet?.id))}
          >
            {createNewVersion ? 'Create New Version' : 'Save Changes'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
